import React, { useState, useEffect, useRef, useMemo } from 'react';
import { MapContainer, TileLayer, GeoJSON, ImageOverlay, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import Product4Legend from './Product4Legend';
import bgImage from '../assets/images/layers.png';

// Tile layer options
const tileLayers = {
    osm: {
        url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
        attribution: '&copy; OpenStreetMap contributors',
        icon: '../assets/images/StreetMap.png',
        label: 'Street Map',
    },
    terrain: {
        url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
        attribution: '&copy; OpenTopoMap contributors',
        icon: '../assets/images/TerrainMap.png',
        label: 'Terrain',
    },
    satellite: {
        url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        attribution: '&copy; Esri & NASA Earthstar Geographics',
        icon: '../assets/images/SatellitesMap.png',
        label: 'Satellite',
    },
};

// Component to handle bounding box zoom
const BboxZoom = ({ bounds }) => {
    const map = useMap();
    const hasZoomedRef = useRef(false); // Track if zoom has been applied for the current bounds

    useEffect(() => {
        if (bounds && bounds.isValid() && !hasZoomedRef.current && map) {
            console.log("BboxZoom triggered with bounds:", bounds);
            // Fit the map to the bounds
            map.fitBounds(bounds, { padding: [20, 20], animate: false });
            // Calculate the center of the bounds
            const boundsCenter = bounds.getCenter();
            console.log("Bounds center:", boundsCenter);
            // Increment zoom by 1, centered on bounds' center
            setTimeout(() => {
                const newZoom = map.getZoom() + 1;
                console.log("Zooming to:", newZoom, "Centered at:", boundsCenter);
                map.setView(boundsCenter, newZoom, { animate: false });
                // Shift the map content 170px to the left
                map.panBy([170, 0], { animate: false }); // Negative x value moves left
                hasZoomedRef.current = true; // Mark zoom as applied
            }, 300);
        }
    }, [bounds, map]);

    // Reset hasZoomedRef when bounds change
    useEffect(() => {
        hasZoomedRef.current = false;
    }, [bounds]);

    return null;
};

// Component to handle fallback zoom
const MapViewUpdater = () => {
    const map = useMap();
    useEffect(() => {
        map.setView([0.0, 20.0], 4); // Fallback coordinates and zoom
        map.panBy([170, 0], { animate: false }); // Shift 170px to the left by default
        map.invalidateSize();
    }, [map]);
    return null;
};

const Product4Map = ({ selectedDataMap }) => {
    const [geoJsonData, setGeoJsonData] = useState(selectedDataMap?.geojson_data);
    const [riskHazard, setRiskHazard] = useState(selectedDataMap?.legend ? true : false);
    const [bbox, setBbox] = useState(null);
    const [showTileOptions, setShowTileOptions] = useState(false); // State for toggling tile options
    const [opacityMap, setOpacityMap] = useState({ layer: 0.8 }); // Single opacity value for the entire layer
    const geoJsonLayerRef = useRef(null);
    const imageOverlayRef = useRef(null); // Ref for ImageOverlay
    const mapRef = useRef(null);
    const colorPallet = [
        { value: 0.0, color: '#FFFFBF' }, // Light Yellow
        { value: 0.1, color: '#FFD978' },
        { value: 0.2, color: '#F7C652' },
        { value: 0.3, color: '#E3A136' },
        { value: 0.4, color: '#C07A22' },
        { value: 0.5, color: '#9D5218' },
        { value: 0.6, color: '#803416' },
        { value: 0.7, color: '#652213' },
        { value: 0.8, color: '#511910' },
        { value: 0.9, color: '#4A130C' },
        { value: 1.0, color: '#66170E' }, // Dark Red
    ];
    const [legendData, setLegendData] = useState(colorPallet);
    const [tileLayer, setTileLayer] = useState('osm'); // Default to OpenStreetMap

    // Initialize opacity map
    useEffect(() => {
        setOpacityMap({ layer: 0.8 }); // Default opacity for both GeoJSON and ImageOverlay
    }, [riskHazard]);

    // Handle opacity change from Legend component
    const handleOpacityChange = (newOpacity) => {
        setOpacityMap({ layer: parseFloat(newOpacity) });
        if (!riskHazard && geoJsonLayerRef.current) {
            geoJsonLayerRef.current.eachLayer((layer) => {
                layer.setStyle({
                    fillOpacity: parseFloat(newOpacity),
                });
            });
        } else if (riskHazard && imageOverlayRef.current) {
            imageOverlayRef.current.setOpacity(parseFloat(newOpacity));
        }
    };

    // Toggle tile options visibility
    const toggleTileOptions = () => {
        setShowTileOptions((prev) => !prev);
    };

    // Calculate bounding box for GeoJSON
    const getBbox = () => {
        console.log("getBbox called with:", { selectedDataMap });
        // Use selectedDataMap.bbox as fallback if GeoJSON calculation fails
        if (selectedDataMap?.bbox) {
            try {
                const bounds = L.latLngBounds([
                    [selectedDataMap.bbox[1], selectedDataMap.bbox[0]], // [South, West]
                    [selectedDataMap.bbox[3], selectedDataMap.bbox[2]], // [North, East]
                ]);
                if (bounds.isValid()) {
                    setBbox(bounds);
                    console.log('Using selectedDataMap.bbox:', bounds);
                    return;
                }
            } catch (error) {
                console.error('Error parsing selectedDataMap.bbox:', error);
            }
        }

        // Try calculating bounds from geoJsonData
        if (geoJsonData && geoJsonData.features?.length > 0) {
            try {
                const layer = L.geoJSON(geoJsonData);
                const bounds = layer.getBounds();
                if (bounds.isValid()) {
                    setBbox(bounds);
                    console.log('Calculated GeoJSON bounds:', bounds);
                } else {
                    setBbox(null);
                    console.warn('Invalid bounds calculated for GeoJSON data');
                }
            } catch (error) {
                console.error('Error calculating GeoJSON bounds:', error);
                setBbox(null);
            }
        } else {
            setBbox(null);
            console.warn('No GeoJSON data available for bounding box calculation');
        }
    };

    // Update geoJsonData and riskHazard when selectedDataMap changes
    useEffect(() => {
        console.log("selectedDataMap updated:", selectedDataMap);
        if (selectedDataMap?.geojson_data) {
            setGeoJsonData(selectedDataMap.geojson_data);
            if (selectedDataMap.legend && selectedDataMap.legend.length > 0) {
                setLegendData(selectedDataMap.legend);
                setRiskHazard(true);
            } else {
                getLegend();
                setRiskHazard(false);
            }
        } else {
            setGeoJsonData(null);
            setBbox(null);
        }
    }, [selectedDataMap]);

    // Calculate bbox when geoJsonData or selectedDataMap changes
    useEffect(() => {
        getBbox();
    }, [geoJsonData, selectedDataMap]);

    const getLegend = () => {
        if (!selectedDataMap?.geojson_data?.features) return;
        const flowValues = selectedDataMap.geojson_data.features.map(item => item.properties.value ?? 0);
        const minFlow = Math.min(...flowValues, 0); // Include 0 as a fallback
        const maxFlow = Math.max(...flowValues);
        if (maxFlow <= 1) {
            setLegendData(colorPallet);
        } else {
            const dynamicLegend = generateDynamicLegend(flowValues, colorPallet);
            setLegendData(dynamicLegend);
        }
    };

    const formatNumberWithSuffix = (num) => {
        if (num >= 1e12) {
            return (num / 1e12).toFixed(1).replace(/\.0$/, '') + 'T'; // Trillions
        } else if (num >= 1e9) {
            return (num / 1e9).toFixed(1).replace(/\.0$/, '') + 'B'; // Billions
        } else if (num >= 1e6) {
            return (num / 1e6).toFixed(1).replace(/\.0$/, '') + 'M'; // Millions
        } else if (num >= 1e3) {
            return (num / 1e3).toFixed(1).replace(/\.0$/, '') + 'K'; // Thousands
        }
        return num.toString(); // Keep as-is if less than 1K
    };

    const generateDynamicLegend = (flowValues, colorPallet) => {
        const minFlow = Math.min(...flowValues, 0); // Ensure 0 is included
        const maxFlow = Math.max(...flowValues);

        if (maxFlow <= 1) {
            return colorPallet;
        }

        const step = (maxFlow - minFlow) / 10;
        let dynamicLegend = [];

        for (let i = 0; i < 10; i++) {
            const value = Math.round(minFlow + step * (i + 1));
            dynamicLegend.push({
                value: formatNumberWithSuffix(value),
                color: colorPallet[i].color,
            });
        }

        return dynamicLegend;
    };

    // Generate a unique key for the MapContainer when data changes
    const mapKey = useMemo(() => JSON.stringify({ geoJsonData }), [geoJsonData]);

    const hoverStyle = {
        color: 'red',
        weight: 1,
        fillOpacity: 1,
    };

    const collarPallet = (colorvalue) => {
        let colors = legendData.map(item => {
            const valueStr = String(item.value); // Ensure it's a string
            const numericValue = parseFloat(valueStr.replace(/[^\d.]/g, '')) * (
                valueStr.includes('T') ? 1e12 :
                    valueStr.includes('B') ? 1e9 :
                        valueStr.includes('M') ? 1e6 :
                            valueStr.includes('K') ? 1e3 :
                                1
            );

            return { ...item, numericValue };
        });

        // Ensure colors are sorted in ascending order of numeric values
        colors.sort((a, b) => a.numericValue - b.numericValue);

        for (let i = 0; i < colors.length - 1; i++) {
            if (colorvalue <= colors[i].numericValue) {
                return colors[i].color; // Return the current lowest color
            }
            if (colorvalue > colors[i].numericValue && colorvalue <= colors[i + 1].numericValue) {
                return colors[i + 1].color; // Return the next highest margin's color
            }
        }

        return colorvalue <= 0 ? colors[0].color : colors[colors.length - 1].color;
    };

    const onEachFeature = (feature, layer, type) => {
        let color;
        if (type === 'primary') {
            layer.bindTooltip(() => {
                const countryName = feature.properties.ADM0_NAME || 'Unnamed Country';
                let tooltipText = `Country: ${countryName}`;
                if (feature.properties.value) {
                    const value = formatNumberWithSuffix(feature.properties.value) || 'NA';
                    tooltipText += `<br/> Value: ${value}`;
                }
                return tooltipText;
            });
            color = feature.properties.value ? collarPallet(feature.properties.value) : 'transparent';
            feature.properties.color = color;
        } else {
            layer.bindTooltip(feature.properties.MAJ_NAME || 'Unnamed feature');
            color = feature.properties.color || 'transparent';
        }

        if (feature.geometry.type === 'Polygon' || feature.geometry.type === 'MultiPolygon') {
            layer.setStyle({
                color: type === 'primary' ? color : 'red',
                weight: 0.5,
                fillColor: color,
                fillOpacity: opacityMap.layer !== undefined ? opacityMap.layer : 0.8,
            });

            layer.on('mouseover', function () {
                layer.setStyle({
                    ...hoverStyle,
                });
            });

            layer.on('mouseout', function () {
                layer.setStyle({
                    color: type === 'primary' ? color : 'red',
                    weight: 0.5,
                    fillColor: color,
                    fillOpacity: opacityMap.layer !== undefined ? opacityMap.layer : 0.8,
                });
            });
        } else if (feature.geometry.type === 'Point') {
            if (type === 'primary') {
                layer.bindTooltip('Point feature: ' + feature.properties.name || 'Unnamed Point');
            } else {
                layer.bindTooltip('Point feature: ' + feature.properties.MAJ_NAME || 'Unnamed Point');
            }
        }
    };

    // Update layer opacity when opacityMap changes
    useEffect(() => {
        if (!riskHazard && geoJsonLayerRef.current && opacityMap.layer !== undefined) {
            geoJsonLayerRef.current.eachLayer((layer) => {
                layer.setStyle({
                    fillOpacity: opacityMap.layer,
                });
            });
        } else if (riskHazard && imageOverlayRef.current && opacityMap.layer !== undefined) {
            imageOverlayRef.current.setOpacity(opacityMap.layer);
        }
    }, [opacityMap, riskHazard]);

    useEffect(() => {
        const observer = new MutationObserver(() => {
            const paths = document.querySelectorAll('path.leaflet-interactive');
            paths.forEach((path) => {
                path.style.outline = 'none';
            });
        });

        const mapContainer = document.querySelector('.leaflet-container');
        if (mapContainer) {
            observer.observe(mapContainer, { childList: true, subtree: true });
        }

        return () => observer.disconnect();
    }, [selectedDataMap]);

    const base64ToDataUrl = (base64) => `data:image/png;base64,${base64}`;

    return (
        <>
            <div style={{ height: '81vh' }}>
                {showTileOptions && (
                    <div className="card legendCard seven border-0 bg-transparent">
                        <div className="card-header bg-white px-1 pt-1 border-0 d-flex pb-1 align-items-center btnGroup">
                            {Object.entries(tileLayers).map(([key, { label, icon }]) => (
                                <button
                                    key={key}
                                    onClick={() => setTileLayer(key)}
                                    className={`btn border-0 ${tileLayer === key ? 'active' : ''}`}
                                    style={{ marginRight: '5px' }}
                                    aria-label={`Switch to ${label} map`}
                                    aria-pressed={tileLayer === key}
                                >
                                    <span>
                                        <img src={icon} loading="lazy" alt={`${label} map icon`} height="26px" />
                                    </span>{' '}
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                <MapContainer
                    key={mapKey}
                    center={[0.0, 20.0]} // Default center
                    zoom={4} // Default zoom
                    style={{ height: '100vh', width: '100%', marginTop: '0px', position: 'fixed' }}
                    ref={mapRef}
                    preferCanvas={true} // Enable canvas rendering
                    whenReady={() => {
                        console.log("MapContainer is ready");
                    }}
                >
                    {bbox && <BboxZoom bounds={bbox} />}
                    {!bbox && <MapViewUpdater />}
                    <TileLayer
                        url={tileLayers[tileLayer].url}
                        attribution={tileLayers[tileLayer].attribution}
                    />

                    {geoJsonData && (
                        <GeoJSON
                            key={JSON.stringify(geoJsonData)}
                            data={geoJsonData}
                            onEachFeature={(feature, layer) => onEachFeature(feature, layer, 'primary')}
                            ref={geoJsonLayerRef}
                        />
                    )}

                    {selectedDataMap?.base64_layer && selectedDataMap?.bbox && (
                        <ImageOverlay
                            url={base64ToDataUrl(selectedDataMap.base64_layer)}
                            bounds={[
                                [selectedDataMap.bbox[1], selectedDataMap.bbox[0]], // [South, West]
                                [selectedDataMap.bbox[3], selectedDataMap.bbox[2]], // [North, East]
                            ]}
                            opacity={opacityMap.layer !== undefined ? opacityMap.layer : 0.8}
                            ref={imageOverlayRef}
                        />
                    )}
                </MapContainer>

                {selectedDataMap && legendData && (
                    <Product4Legend legendData={legendData} onOpacityChange={handleOpacityChange} riskHazard={riskHazard} />
                )}
            </div>
            <div
                className="card legendCard ten border-0"
                style={{
                    backgroundImage: `url(${bgImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    fontSize: '12px !important',
                    fontWeight: '600 !important',
                    position: 'absolute',
                    bottom: '12px',
                    left: '330px',
                    cursor: 'pointer',
                    backgroundColor: 'transparent'
                }}
                onClick={toggleTileOptions}
            >
                <p className="mb-0 text-white">
                    <span><img src="../assets/images/layers1.png" /></span> Layers
                </p>
            </div>
        </>
    );
};

export default Product4Map;